import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  HeartHandshake, 
  Upload, 
  Clock, 
  Plus, 
  Trash2, 
  Image as ImageIcon,
  Save,
  Award,
  Edit3,
  X,
  CheckCircle2,
  Layers,
  ListPlus
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const COLOR_OPTIONS = [
  { label: 'Ungu / Royal Purple', color: '#7c3aed', colorLight: '#ede9fe' },
  { label: 'Cyan / Ocean Blue', color: '#06b6d4', colorLight: '#cffafe' },
  { label: 'Amber / Warm Gold', color: '#f59e0b', colorLight: '#fef3c7' },
  { label: 'Emerald / Green', color: '#10b981', colorLight: '#d1fae5' },
  { label: 'Biru Klasik / Navy', color: '#2563eb', colorLight: '#eff6ff' },
  { label: 'Rose / Pink Accent', color: '#e11d48', colorLight: '#ffe4e6' }
];

const PROGRAM_ICON_OPTIONS = [
  { value: 'fa-people-group', label: 'Komunitas / Keluarga (Family & Community)' },
  { value: 'fa-book-open', label: 'Buku Terbuka (Learning & Workshops)' },
  { value: 'fa-handshake', label: 'Jabat Tangan (Collaboration & Partnership)' },
  { value: 'fa-users', label: 'Kelompok Siswa / Orang Tua (Peer Connection)' },
  { value: 'fa-comments', label: 'Diskusi / Percakapan (Open Dialogue)' },
  { value: 'fa-heart', label: 'Hati / Dukungan Emosional (Emotional Support)' },
  { value: 'fa-lightbulb', label: 'Lampu / Ide & Wawasan (Insights & Innovation)' },
  { value: 'fa-graduation-cap', label: 'Pendidikan / Akademik (Educational Excellence)' },
  { value: 'fa-brain', label: 'Otak / Strategi Belajar (Learning Strategies)' },
  { value: 'fa-calendar-check', label: 'Kalender (Kegiatan & Event Coordination)' },
  { value: 'fa-hands-helping', label: 'Tangan Membantu (Proyek Sosial / Volunteering)' },
  { value: 'fa-star', label: 'Bintang (Kepemimpinan / Signature Program)' }
];

export default function ParentsLearningCommunityForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Parents Learning Community',
    title: 'Growing Together Through Shared Learning & Support',
    subtitle: 'A vibrant collaborative space where families connect, learn, and actively contribute to our school community. We believe that strong parent engagement creates the foundation for student success.',
    stats: {
      active_parents_count: '500+',
      active_parents_label: 'Active Parents',
      annual_events_count: '24',
      annual_events_label: 'Annual Events',
      learning_programs_count: '12',
      learning_programs_label: 'Learning Programs'
    },
    items: [
      {
        id: 'palc-support',
        title: 'Family Support Circles',
        subtitle: 'Building Connections & Peer Support',
        description: 'Monthly gatherings to connect families, share insights, and build a supportive parent network.',
        color: '#7c3aed',
        colorLight: '#ede9fe',
        icon: 'fa-people-group',
        date: 'Monthly',
        frequency: 'Every 2nd Saturday',
        image: '/assets/news/news3.jpg',
        images: ['/assets/news/news3.jpg', '', ''],
        benefits: 'Monthly gatherings, Peer support network, Expert facilitators',
        content: 'Family Support Circles are the heart of our Parents Learning Community. These monthly gatherings create a safe, welcoming space where parents can connect with others, share their experiences, and receive support from peers who understand the joys and challenges of parenting.',
        features: [
          { icon: 'fa-users', title: 'Peer Connection', detail: 'Meet other parents, share experiences, and build meaningful relationships.' },
          { icon: 'fa-comments', title: 'Open Discussions', detail: 'Facilitated conversations on parenting challenges, child development, and school life.' }
        ],
        highlights: [
          '50+ families participate in monthly gatherings',
          'Expert facilitators guide meaningful discussions',
          'Refreshments and childcare provided'
        ],
        is_featured: true
      },
      {
        id: 'palc-workshops',
        title: 'Learning Workshops',
        subtitle: 'Empowering Parents Through Education',
        description: 'Interactive sessions on learning strategies, wellbeing, and student development at home.',
        color: '#06b6d4',
        colorLight: '#cffafe',
        icon: 'fa-book-open',
        date: 'Quarterly',
        frequency: 'Every 3 months',
        image: '/assets/news/news4.jpg',
        images: ['/assets/news/news4.jpg', '', ''],
        benefits: '',
        content: 'Our Learning Workshops are designed to empower parents with the knowledge and tools needed to support their children academic and personal development.',
        features: [
          { icon: 'fa-brain', title: 'Learning Strategies', detail: 'Discover effective techniques to support your child growth and foster a love of learning.' }
        ],
        highlights: [
          '8+ workshops offered annually',
          'Expert speakers from education and psychology fields'
        ],
        is_featured: false
      },
      {
        id: 'palc-community',
        title: 'Community Collaboration',
        subtitle: 'Parents & School Working Together',
        description: 'Joint initiatives that connect parents with school programs, events, and service projects.',
        color: '#f59e0b',
        colorLight: '#fef3c7',
        icon: 'fa-handshake',
        date: 'Ongoing',
        frequency: 'Throughout the year',
        image: '/assets/news/news7.jpg',
        images: ['/assets/news/news7.jpg', '', ''],
        benefits: '',
        content: 'Community Collaboration brings parents and school together in meaningful ways. Through volunteer opportunities, service projects, and collaborative initiatives, parents become active partners.',
        features: [
          { icon: 'fa-calendar-check', title: 'Event Coordination', detail: 'Parents volunteer to organize and lead school events, celebrations, and community gatherings.' }
        ],
        highlights: [
          '200+ parent volunteers annually',
          '15+ community service projects per year'
        ],
        is_featured: false
      }
    ],
    benefits: [
      { title: 'Continuous Learning', description: 'Access workshops and resources on parenting, child development, and educational strategies.' },
      { title: 'Community Connection', description: 'Build meaningful relationships with other families and create a supportive parent network.' },
      { title: 'Student Success', description: "Strengthen home-school partnership to directly support your child's academic and personal growth." },
      { title: 'Leadership Opportunities', description: 'Volunteer, lead initiatives, and make a meaningful impact on our school community.' }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    subtitle: false,
    stats: false,
    benefits: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [mediaMap, setMediaMap] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Modal Editor state for PaLC Detail Program
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [slideUploading, setSlideUploading] = useState({ 0: false, 1: false, 2: false });
  const [slideUploadProgress, setSlideUploadProgress] = useState({ 0: 0, 1: 0, 2: 0 });

  // Temp states for adding new highlight or feature inside modal
  const [newHighlightText, setNewHighlightText] = useState('');
  const [newFeatureIcon, setNewFeatureIcon] = useState('fa-users');
  const [newFeatureTitle, setNewFeatureTitle] = useState('');
  const [newFeatureDetail, setNewFeatureDetail] = useState('');

  // Fetch mediaMap from server for instant ID resolution
  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        if (json.ok && Array.isArray(json.data)) {
          const map = {};
          const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
          json.data.forEach(m => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${origin}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          setMediaMap(map);
          try {
            sessionStorage.setItem('cms_media_map', JSON.stringify(map));
          } catch (e) {}
        }
      }
    } catch (e) {
      console.warn('[ParentsLearningCommunityForm] Media map fetch error:', e);
    }
  }, [apiBase]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=parents`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          s.section_type === 'parents-palc' || 
          s.section_type === 'parents-learning-community' || 
          s.section_type === 'palc'
        );
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            s.section_type === 'parents-palc' || 
            s.section_type === 'parents-learning-community' || 
            s.section_type === 'palc'
          );
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 
          ? en.items 
          : (Array.isArray(en.parents_learning_community) && en.parents_learning_community.length > 0 ? en.parents_learning_community : formData.items);

        const loadedItems = rawItems.map((item, idx) => {
          const defaultColor = idx === 0 ? '#7c3aed' : (idx === 1 ? '#06b6d4' : '#f59e0b');
          const defaultLight = idx === 0 ? '#ede9fe' : (idx === 1 ? '#cffafe' : '#fef3c7');
          const defaultIcon = idx === 0 ? 'fa-people-group' : (idx === 1 ? 'fa-book-open' : 'fa-handshake');

          let benefitsStr = '';
          if (Array.isArray(item.benefits)) {
            benefitsStr = item.benefits.join(', ');
          } else if (typeof item.benefits === 'string') {
            benefitsStr = item.benefits;
          }

          let imagesArr = [];
          if (Array.isArray(item.images) && item.images.length > 0) {
            imagesArr = [...item.images];
          } else if (item.image) {
            imagesArr = [item.image, '', ''];
          } else {
            imagesArr = ['', '', ''];
          }
          while (imagesArr.length < 3) imagesArr.push('');

          return {
            id: item.id || `palc-${idx + 1}`,
            title: item.title || '',
            subtitle: item.subtitle || (idx === 0 ? 'Building Connections & Peer Support' : ''),
            description: item.description || '',
            color: item.color || defaultColor,
            colorLight: item.colorLight || defaultLight,
            icon: item.icon || defaultIcon,
            date: item.date || (idx === 0 ? 'Monthly' : (idx === 1 ? 'Quarterly' : 'Ongoing')),
            frequency: item.frequency || (idx === 0 ? 'Every 2nd Saturday' : (idx === 1 ? 'Every 3 months' : 'Throughout the year')),
            image: item.image || imagesArr[0] || '',
            images: imagesArr,
            benefits: benefitsStr,
            content: item.content || item.description || '',
            features: Array.isArray(item.features) ? item.features : [],
            highlights: Array.isArray(item.highlights) ? item.highlights : (typeof item.highlights === 'string' ? [item.highlights] : []),
            is_featured: idx === 0 || !!item.is_featured
          };
        });

        const loadedData = {
          kicker: en.kicker || 'Parents Learning Community',
          title: en.title || 'Growing Together Through Shared Learning & Support',
          subtitle: en.subtitle || en.description || formData.subtitle,
          stats: en.stats || formData.stats,
          items: loadedItems,
          benefits: Array.isArray(en.benefits) && en.benefits.length > 0 ? en.benefits : formData.benefits
        };

        setFormData(loadedData);
        setOriginalData(loadedData);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, subtitle: false, stats: false, benefits: false };
              const pItems = {};
              const pDeletes = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'subtitle', 'stats', 'benefits'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)(?:\.(add|update|delete))?/);
                    if (m) {
                      const idx = m[1];
                      const op = m[2] || 'update';
                      if (op === 'delete') {
                        pDeletes[`item_${idx}`] = true;
                      } else {
                        pItems[`item_${idx}`] = true;
                      }
                    }
                  } else if (c.field_id === 'items') {
                    pFields.items = true;
                  }
                }
              });

              setPendingFields(pFields);
              setPendingItems(pItems);
              setPendingDeletes(pDeletes);
            }
          }
        } catch (pe) {
          console.warn('[ParentsLearningCommunityForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[ParentsLearningCommunityForm] Load error:', err);
      toast.error('Failed to load Learning Community content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStatChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      stats: { ...prev.stats, [key]: value }
    }));
  };

  const handleBenefitChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.benefits];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, benefits: next };
    });
  };

  // Open modal editor for a program
  const handleOpenEditModal = (index = null) => {
    if (index !== null) {
      const cur = formData.items[index];
      const curImages = Array.isArray(cur.images) ? [...cur.images] : (cur.image ? [cur.image] : ['', '', '']);
      while (curImages.length < 3) curImages.push('');

      setEditingIndex(index);
      setEditingItem({
        ...cur,
        subtitle: cur.subtitle || '',
        color: cur.color || '#7c3aed',
        colorLight: cur.colorLight || '#ede9fe',
        icon: cur.icon || 'fa-people-group',
        date: cur.date || 'Monthly',
        frequency: cur.frequency || 'Every 2nd Saturday',
        content: cur.content || cur.description || '',
        features: Array.isArray(cur.features) ? [...cur.features] : [],
        highlights: Array.isArray(cur.highlights) ? [...cur.highlights] : [],
        benefits: cur.benefits || '',
        images: curImages,
        image: curImages[0] || cur.image || ''
      });
    } else {
      // Create new program
      setEditingIndex(null);
      setEditingItem({
        id: `palc-prog-${Date.now()}`,
        title: '',
        subtitle: '',
        description: '',
        color: '#7c3aed',
        colorLight: '#ede9fe',
        icon: 'fa-people-group',
        date: 'Monthly',
        frequency: 'Every 2nd Saturday',
        content: '',
        features: [],
        highlights: [],
        benefits: '',
        images: ['', '', ''],
        image: '',
        is_featured: false
      });
    }

    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setSlideUploadProgress({ 0: 0, 1: 0, 2: 0 });
    setEditorModalOpen(true);
  };

  // Upload image for slide inside modal (0, 1, or 2)
  const handleSlideImageUpload = (slideIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format foto harus JPG, PNG, atau WebP');
      return;
    }

    setSlideUploading(prev => ({ ...prev, [slideIndex]: true }));
    setSlideUploadProgress(prev => ({ ...prev, [slideIndex]: 10 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (err) {}
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setSlideUploadProgress(prev => ({ ...prev, [slideIndex]: pct }));
      }
    };

    xhr.onload = () => {
      setSlideUploading(prev => ({ ...prev, [slideIndex]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          let mediaUrl = data.url || data.file_path || data.path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          const finalImageValue = mediaUrl || (mediaId ? String(mediaId) : '');

          if (finalImageValue) {
            setEditingItem(prev => {
              const curImages = Array.isArray(prev.images) ? [...prev.images] : ['', '', ''];
              curImages[slideIndex] = finalImageValue;
              return {
                ...prev,
                images: curImages,
                image: slideIndex === 0 ? finalImageValue : (prev.image || finalImageValue)
              };
            });

            if (mediaUrl && mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: mediaUrl,
                [String(mediaId)]: mediaUrl
              }));
            }
            toast.success(`Foto Slide ${slideIndex + 1} berhasil diunggah`);
          } else {
            toast.error('Gagal membaca ID media');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setSlideUploading(prev => ({ ...prev, [slideIndex]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleRemoveSlideImage = (slideIndex) => {
    setEditingItem(prev => {
      const curImages = Array.isArray(prev.images) ? [...prev.images] : ['', '', ''];
      curImages[slideIndex] = '';
      const firstAvailable = curImages.find(Boolean) || '';
      return {
        ...prev,
        images: curImages,
        image: slideIndex === 0 ? firstAvailable : prev.image
      };
    });
    toast.info(`Foto Slide ${slideIndex + 1} dihapus`);
  };

  // Add bullet point highlight in modal
  const handleAddHighlight = () => {
    if (!newHighlightText.trim()) return;
    setEditingItem(prev => ({
      ...prev,
      highlights: [...(prev.highlights || []), newHighlightText.trim()]
    }));
    setNewHighlightText('');
    toast.success('Poin sorotan ditambahkan');
  };

  const handleRemoveHighlight = (hIdx) => {
    setEditingItem(prev => ({
      ...prev,
      highlights: (prev.highlights || []).filter((_, idx) => idx !== hIdx)
    }));
  };

  // Add structured Feature item in modal ("What You'll Experience")
  const handleAddFeature = () => {
    if (!newFeatureTitle.trim()) {
      toast.warning('Silakan isi judul pengalaman / pilar terlebih dahulu');
      return;
    }
    const newFeature = {
      icon: newFeatureIcon || 'fa-users',
      title: newFeatureTitle.trim(),
      detail: (newFeatureDetail || '').trim()
    };
    setEditingItem(prev => ({
      ...prev,
      features: [...(prev.features || []), newFeature]
    }));
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    toast.success(`Pengalaman "${newFeature.title}" berhasil dimasukkan ke daftar`);
  };

  const handleRemoveFeature = (fIdx) => {
    setEditingItem(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== fIdx)
    }));
  };

  // Save changes from modal into local formData
  const handleSaveModalChanges = () => {
    if (!editingItem?.title?.trim()) {
      toast.warning('Silakan isi judul program terlebih dahulu');
      return false;
    }

    const rawImgs = Array.isArray(editingItem?.images) ? editingItem.images : [editingItem?.image];
    const cleanedImages = rawImgs.filter(Boolean).map(img => {
      const resolved = resolveImage(img) || (mediaMap && (mediaMap[img] || mediaMap[String(img)])) || img;
      if (typeof resolved === 'string' && (resolved.startsWith('http://') || resolved.startsWith('https://'))) {
        return resolved;
      }
      if (typeof resolved === 'string' && resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
        return `${origin}${resolved}`;
      }
      return resolved;
    });

    if (cleanedImages.length === 0 && !editingItem?.image) {
      toast.warning('Silakan upload minimal foto sampul (Slide 1) terlebih dahulu');
      return false;
    }

    if (!editingItem?.description?.trim()) {
      toast.warning('Silakan isi ringkasan program');
      return false;
    }

    // Auto-commit pending highlight if typed but didn't click add
    const finalHighlights = [...(editingItem.highlights || [])];
    if (newHighlightText && newHighlightText.trim()) {
      finalHighlights.push(newHighlightText.trim());
    }

    // Auto-commit pending feature if typed but didn't click add
    const finalFeatures = [...(editingItem.features || [])];
    if (newFeatureTitle && newFeatureTitle.trim()) {
      finalFeatures.push({
        icon: newFeatureIcon || 'fa-users',
        title: newFeatureTitle.trim(),
        detail: (newFeatureDetail || '').trim()
      });
    }

    const slug = editingItem.id || editingItem.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const updatedItem = {
      ...editingItem,
      id: slug,
      title: editingItem.title.trim(),
      subtitle: editingItem.subtitle?.trim() || '',
      description: editingItem.description?.trim() || '',
      color: editingItem.color || '#7c3aed',
      colorLight: editingItem.colorLight || '#ede9fe',
      icon: editingItem.icon || 'fa-people-group',
      date: editingItem.date?.trim() || 'Monthly',
      frequency: editingItem.frequency?.trim() || 'Every 2nd Saturday',
      content: editingItem.content?.trim() || editingItem.description?.trim() || '',
      highlights: finalHighlights.filter(Boolean),
      features: finalFeatures.filter(f => f && (f.title || f.detail)),
      benefits: editingItem.benefits || '',
      image: cleanedImages[0] || editingItem.image || '',
      images: cleanedImages
    };

    let targetIdx = editingIndex;
    if (editingIndex === null) {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, updatedItem]
      }));
      targetIdx = formData.items.length;
      toast.success(`Program "${updatedItem.title}" berhasil ditambahkan`);
    } else {
      setFormData(prev => {
        const next = [...prev.items];
        next[editingIndex] = updatedItem;
        return { ...prev, items: next };
      });
      toast.success(`Perubahan program "${updatedItem.title}" tersimpan di draf`);
    }

    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setEditorModalOpen(false);
    return { item: updatedItem, index: targetIdx };
  };

  // Save modal and directly submit to approval workflow
  const handleSaveAndSubmitModal = async () => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const saved = handleSaveModalChanges();
    if (!saved) return;

    const { item, index } = saved;
    const isNew = editingIndex === null;
    const key = `item_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.${isNew ? 'add' : 'update'}`,
          field_name: isNew ? `Add PaLC Program: ${item.title}` : `PaLC Program: ${item.title}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: item,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Program "${item.title}" berhasil diajukan untuk approval Kepala Sekolah!`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          if (isNew) {
            next.push(item);
          } else {
            next[index] = item;
          }
          return { ...prev, items: next };
        });

        // Invalidate local cache
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Pengajuan gagal');
      }
    } catch (err) {
      console.error('[ParentsLearningCommunityForm] Submit modal error:', err);
      toast.error('Gagal mengajukan program: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveItem = async (index) => {
    if (formData.items.length <= 1) {
      toast.warning('Minimal harus ada 1 program PaLC');
      return;
    }
    const itemToRemove = formData.items[index];
    const isSavedInOriginal = originalData.items?.some(orig => orig.id === itemToRemove.id || orig.title === itemToRemove.title);

    if (!isSavedInOriginal || !sectionId) {
      if (window.confirm(`Hapus program draf "${itemToRemove.title || 'Untitled'}"?`)) {
        setFormData(prev => ({
          ...prev,
          items: prev.items.filter((_, idx) => idx !== index)
        }));
        toast.info(`Program "${itemToRemove.title}" dihapus dari draf`);
      }
      return;
    }

    if (!window.confirm(`Ajukan penghapusan program "${itemToRemove.title || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    const key = `item_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Hapus Program PaLC: ${itemToRemove.title}`,
          old_value: itemToRemove,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Permintaan hapus program "${itemToRemove.title}" berhasil diajukan untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [key]: true }));
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Gagal mengajukan penghapusan');
      }
    } catch (err) {
      console.error('[ParentsLearningCommunityForm] Delete error:', err);
      toast.error('Gagal mengajukan hapus: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Submit single card from the list
  const handleSubmitItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const item = formData.items[index];
    const key = `item_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.update`,
          field_name: `PaLC Program: ${item.title}`,
          old_value: originalData.items?.[index] || {},
          new_value: item,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Program "${item.title}" diajukan untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = item;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsLearningCommunityForm] Submit error:', err);
      toast.error('Gagal mengajukan program: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Submit general fields (kicker, title, subtitle, stats, benefits)
  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsLearningCommunityForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Parents Learning Community content...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <HeartHandshake size={22} />
        </div>
        <div>
          <h2 className="unified-title">Parents Learning Community (PaLC)</h2>
          <p className="unified-subtitle">
            Kelola program kemitraan orang tua, statistik keterlibatan, dan detail lengkap yang tampil di kartu halaman Parents maupun halaman detail PaLC.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Parents Learning Community"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Growing Together Through Shared Learning & Support"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Subtitle */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle / Description</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={pendingFields.subtitle}
            placeholder="Detailed description of the community space..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', formData.subtitle, 'Section Subtitle')}
            disabled={pendingFields.subtitle || submitting.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : (pendingFields.subtitle ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Community Statistics */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Community Statistics</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Data statistik keterlibatan orang tua yang tampil di header bagian PaLC.
            </p>
          </div>
          {pendingFields.stats && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
              Active Parents Number & Label
            </label>
            <input
              type="text"
              className="unified-input"
              value={formData.stats.active_parents_count || ''}
              disabled={pendingFields.stats}
              onChange={(e) => handleStatChange('active_parents_count', e.target.value)}
              placeholder="e.g. 500+"
              style={{ marginBottom: '6px' }}
            />
            <input
              type="text"
              className="unified-input"
              value={formData.stats.active_parents_label || ''}
              disabled={pendingFields.stats}
              onChange={(e) => handleStatChange('active_parents_label', e.target.value)}
              placeholder="Label e.g. Active Parents"
            />
          </div>

          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
              Annual Events Number & Label
            </label>
            <input
              type="text"
              className="unified-input"
              value={formData.stats.annual_events_count || ''}
              disabled={pendingFields.stats}
              onChange={(e) => handleStatChange('annual_events_count', e.target.value)}
              placeholder="e.g. 24"
              style={{ marginBottom: '6px' }}
            />
            <input
              type="text"
              className="unified-input"
              value={formData.stats.annual_events_label || ''}
              disabled={pendingFields.stats}
              onChange={(e) => handleStatChange('annual_events_label', e.target.value)}
              placeholder="Label e.g. Annual Events"
            />
          </div>

          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
              Learning Programs Number & Label
            </label>
            <input
              type="text"
              className="unified-input"
              value={formData.stats.learning_programs_count || ''}
              disabled={pendingFields.stats}
              onChange={(e) => handleStatChange('learning_programs_count', e.target.value)}
              placeholder="e.g. 12"
              style={{ marginBottom: '6px' }}
            />
            <input
              type="text"
              className="unified-input"
              value={formData.stats.learning_programs_label || ''}
              disabled={pendingFields.stats}
              onChange={(e) => handleStatChange('learning_programs_label', e.target.value)}
              placeholder="Label e.g. Learning Programs"
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('stats', formData.stats, 'Community Statistics')}
            disabled={pendingFields.stats || submitting.stats}
          >
            <Save size={14} /> {submitting.stats ? 'Saving...' : (pendingFields.stats ? 'Pending' : 'Save Stats')}
          </button>
        </div>
      </div>

      {/* Programs List Cards */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">PaLC Programs ({formData.items.length})</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Program urutan pertama otomatis tampil sebagai Signature Program (banner besar); program berikutnya tampil pada grid kartu. Klik "Edit Detail" untuk melengkapi foto slideshow, highlight, dan poin pengalaman.
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={() => handleOpenEditModal(null)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}
          >
            <Plus size={16} /> Tambah Program Baru
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[`item_${idx}`];
            const isPendingDelete = pendingDeletes[`item_${idx}`];
            const isSubmitting = submitting[`item_${idx}`];
            const resolvedCover = resolveImage(item.images?.[0] || item.image);

            return (
              <div 
                key={idx} 
                style={{ 
                  border: isPendingDelete ? '1.5px dashed #ef4444' : (isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0'), 
                  borderRadius: '16px', 
                  overflow: 'hidden',
                  background: isPendingDelete ? '#fef2f2' : (isPending ? '#fffdf7' : '#ffffff'),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Media Preview Header */}
                <div style={{ position: 'relative', height: '150px', background: '#0f172a' }}>
                  {resolvedCover ? (
                    <img 
                      src={resolvedCover} 
                      alt={item.title} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                      <ImageIcon size={32} />
                    </div>
                  )}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,0.8) 0%, transparent 70%)' }} />
                  
                  {/* Top Badges */}
                  <div style={{ position: 'absolute', top: '10px', left: '10px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {idx === 0 ? (
                      <span style={{ background: '#7c3aed', color: '#fff', fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px' }}>
                        SIGNATURE
                      </span>
                    ) : (
                      <span style={{ background: 'rgba(15,23,42,0.7)', color: '#fff', fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px' }}>
                        PROGRAM #{idx + 1}
                      </span>
                    )}
                    {item.date && (
                      <span style={{ background: 'rgba(255,255,255,0.9)', color: '#0f172a', fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px' }}>
                        {item.date}
                      </span>
                    )}
                    {isPendingDelete ? (
                      <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5' }}>
                        <Clock size={11} /> Pending Delete
                      </span>
                    ) : isPending ? (
                      <span className="unified-pending-pill">
                        <Clock size={11} /> Pending Approval
                      </span>
                    ) : null}
                  </div>

                  {/* Program Icon & Title over image */}
                  <div style={{ position: 'absolute', bottom: '10px', left: '14px', right: '14px', color: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className={`fas ${item.icon || 'fa-people-group'}`} style={{ color: item.color || '#38bdf8', fontSize: '14px' }} />
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#ffffff', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                        {item.title || '(Belum ada judul)'}
                      </h4>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                  {item.subtitle && (
                    <p style={{ margin: 0, fontSize: '12.5px', color: item.color || '#7c3aed', fontWeight: 600 }}>
                      {item.subtitle}
                    </p>
                  )}

                  <p style={{ margin: 0, fontSize: '12.5px', color: '#475569', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.description || 'Belum ada ringkasan program.'}
                  </p>

                  {/* Highlights count */}
                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: '#64748b', marginTop: 'auto' }}>
                    <span>{(item.features || []).length} Pengalaman</span>
                    <span>•</span>
                    <span>{(item.highlights || []).length} Sorotan</span>
                    {item.frequency && (
                      <>
                        <span>•</span>
                        <span>{item.frequency}</span>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => handleOpenEditModal(idx)}
                        disabled={isPending || isPendingDelete || isSubmitting}
                        style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                      >
                        <Edit3 size={13} /> Edit Detail
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        disabled={isPending || isPendingDelete || isSubmitting}
                        style={{ background: 'none', border: 'none', color: '#ef4444', padding: '6px', cursor: (isPending || isPendingDelete || isSubmitting) ? 'not-allowed' : 'pointer', borderRadius: '4px', opacity: (isPending || isPendingDelete || isSubmitting) ? 0.4 : 1 }}
                        title="Hapus Program"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <button
                      type="button"
                      className="unified-submit-button"
                      onClick={() => handleSubmitItem(idx)}
                      disabled={isPending || isPendingDelete || isSubmitting}
                      style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                      <Save size={13} /> {isSubmitting ? 'Saving...' : isPendingDelete ? 'Pending Delete' : isPending ? 'Pending' : 'Save Card'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Join PaLC Benefits Grid */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={16} color="#2563eb" /> "Why Join PaLC?" 4 Benefits Pillars
            </label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Empat pilar nilai keikutsertaan orang tua yang tampil pada bagian bawah seksi PaLC.
            </p>
          </div>
          {pendingFields.benefits && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '16px', marginTop: '14px' }}>
          {formData.benefits.map((benefit, bIdx) => (
            <div key={bIdx} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>
                Pillar #{bIdx + 1} Title
              </label>
              <input
                type="text"
                className="unified-input"
                value={benefit.title}
                disabled={pendingFields.benefits}
                onChange={(e) => handleBenefitChange(bIdx, 'title', e.target.value)}
                style={{ marginBottom: '8px', fontWeight: 600 }}
              />
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block' }}>
                Description
              </label>
              <textarea
                className="unified-textarea"
                rows={2}
                value={benefit.description}
                disabled={pendingFields.benefits}
                onChange={(e) => handleBenefitChange(bIdx, 'description', e.target.value)}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('benefits', formData.benefits, 'Why Join PaLC Benefits')}
            disabled={pendingFields.benefits || submitting.benefits}
          >
            <Save size={14} /> {submitting.benefits ? 'Saving...' : (pendingFields.benefits ? 'Pending' : 'Save Benefits')}
          </button>
        </div>
      </div>

      {/* FULL MODAL EDITOR FOR PaLC DETAIL PROGRAM */}
      {editorModalOpen && editingItem && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditorModalOpen(false);
          }}
        >
          <div 
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              maxWidth: '820px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 10, borderRadius: '20px 20px 0 0' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                  {editingIndex === null ? 'Tambah Program PaLC Baru' : `Edit Detail Program: ${editingItem.title || 'Untitled'}`}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
                  Lengkapi data judul, foto carousel header, poin pengalaman, dan narasi yang tampil di halaman detail program PaLC.
                </p>
              </div>
              <button 
                type="button" 
                onClick={() => setEditorModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '6px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Form Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Program Title & Subtitle */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="unified-label">Judul Program (Program Title) *</label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.title}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Family Support Circles"
                  />
                </div>
                <div>
                  <label className="unified-label">Subjudul / Topik (Program Subtitle)</label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.subtitle}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="e.g. Building Connections & Peer Support"
                  />
                </div>
              </div>

              {/* Schedule (Date) & Frequency */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="unified-label">Jadwal Pelaksanaan (Schedule / Date) *</label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.date}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, date: e.target.value }))}
                    placeholder="e.g. Monthly, Quarterly, Ongoing"
                  />
                </div>
                <div>
                  <label className="unified-label">Frekuensi / Waktu Spesifik (Frequency) *</label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.frequency}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, frequency: e.target.value }))}
                    placeholder="e.g. Every 2nd Saturday, Every 3 months"
                  />
                </div>
              </div>

              {/* Theme Color & Icon Picker */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="unified-label">Warna Aksen Halaman Detail (Theme Color)</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {COLOR_OPTIONS.map((c, cIdx) => (
                      <button
                        key={cIdx}
                        type="button"
                        onClick={() => setEditingItem(prev => ({ ...prev, color: c.color, colorLight: c.colorLight }))}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: c.color,
                          border: editingItem.color === c.color ? '3px solid #0f172a' : '2px solid transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff'
                        }}
                        title={c.label}
                      >
                        {editingItem.color === c.color && <CheckCircle2 size={16} />}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="unified-label">Ikon Program (Program Icon)</label>
                  <select
                    className="unified-input"
                    value={editingItem.icon || 'fa-people-group'}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, icon: e.target.value }))}
                  >
                    {PROGRAM_ICON_OPTIONS.map((opt, oIdx) => (
                      <option key={oIdx} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Header Slideshow (Up to 3 Photos) */}
              <div>
                <label className="unified-label">
                  Foto Header Slideshow (Maksimal 3 Foto)
                </label>
                <p style={{ margin: '2px 0 10px 0', fontSize: '12px', color: '#64748b' }}>
                  Slide 1 berfungsi sebagai cover kartu di halaman utama. Slide 1, 2, dan 3 akan membentuk carousel slideshow otomatis pada header halaman detail program.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                  {[0, 1, 2].map((slideIdx) => {
                    const slideImg = editingItem.images?.[slideIdx];
                    const previewSlideUrl = resolveImage(slideImg);
                    const isUploading = slideUploading[slideIdx];
                    const progress = slideUploadProgress[slideIdx] || 0;

                    return (
                      <div 
                        key={slideIdx}
                        style={{
                          border: '1px solid #cbd5e1',
                          borderRadius: '12px',
                          padding: '12px',
                          background: '#f8fafc',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#334155' }}>
                            {slideIdx === 0 ? 'Slide 1: Cover Utama *' : `Slide ${slideIdx + 1}: Carousel`}
                          </span>
                          {slideImg && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSlideImage(slideIdx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                              title="Hapus Foto Ini"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>

                        <div 
                          style={{
                            height: '110px',
                            borderRadius: '8px',
                            background: '#e2e8f0',
                            overflow: 'hidden',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {previewSlideUrl ? (
                            <img 
                              src={previewSlideUrl} 
                              alt={`Slide ${slideIdx + 1}`} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                          ) : (
                            <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                              <ImageIcon size={24} style={{ margin: '0 auto 4px auto' }} />
                              <div style={{ fontSize: '11px' }}>Slide {slideIdx + 1}</div>
                            </div>
                          )}

                          {isUploading && (
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                              <div className="loading-spinner" style={{ width: '18px', height: '18px', marginBottom: '6px' }} />
                              <span style={{ fontSize: '11px', fontWeight: 600 }}>{progress}%</span>
                            </div>
                          )}
                        </div>

                        <label 
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px',
                            padding: '6px',
                            background: '#fff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            fontWeight: 600,
                            color: '#2563eb',
                            cursor: isUploading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <Upload size={12} />
                          {isUploading ? 'Uploading...' : (slideImg ? 'Ganti Foto' : 'Unggah Foto')}
                          <input 
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            style={{ display: 'none' }}
                            disabled={isUploading}
                            onChange={(e) => handleSlideImageUpload(slideIdx, e)}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Short Summary (Overview on Card & Detail Page) */}
              <div>
                <label className="unified-label">Ringkasan Program / Lead Overview (Tampil di Kartu & Overview) *</label>
                <textarea
                  className="unified-textarea"
                  rows={2}
                  value={editingItem.description}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ringkasan singkat 2-3 kalimat tentang manfaat program ini bagi orang tua..."
                />
              </div>

              {/* Key Benefits (if Signature Program) */}
              <div>
                <label className="unified-label">Poin Kunci Banner (Key Benefits, dipisahkan koma)</label>
                <input
                  type="text"
                  className="unified-input"
                  value={editingItem.benefits || ''}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, benefits: e.target.value }))}
                  placeholder="e.g. Monthly gatherings, Peer support network, Expert facilitators"
                />
              </div>

              {/* Full Program Story / Narrative */}
              <div>
                <label className="unified-label">Narasi Program Lengkap (About This Program Content) *</label>
                <textarea
                  className="unified-textarea"
                  rows={4}
                  value={editingItem.content}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Ceritakan latar belakang, tujuan, dan bagaimana orang tua dapat terlibat secara mendalam..."
                />
              </div>

              {/* Program Highlights Checklist */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label className="unified-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <ListPlus size={16} color="#7c3aed" /> Program Highlights (Poin Sorotan Program)
                  </label>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    {(editingItem.highlights || []).length} Poin Tersimpan
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newHighlightText}
                    onChange={(e) => setNewHighlightText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddHighlight();
                      }
                    }}
                    placeholder="Tulis sorotan baru (misal: '50+ families participate in monthly gatherings')..."
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddHighlight}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Tambah Poin
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {(editingItem.highlights || []).map((h, hIdx) => (
                    <div key={hIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b' }}>
                        <CheckCircle2 size={15} color="#10b981" /> {h}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveHighlight(hIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* What You'll Experience Features */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label className="unified-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Layers size={16} color="#06b6d4" /> "What You'll Experience" Features
                  </label>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    {(editingItem.features || []).length} Pengalaman Tersimpan
                  </span>
                </div>

                {/* List of existing features */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                  {(editingItem.features || []).map((feat, fIdx) => (
                    <div key={fIdx} style={{ background: '#fff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
                          <i className={`fas ${feat.icon || 'fa-users'}`} style={{ color: editingItem.color || '#7c3aed' }} />
                          {feat.title}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(fIdx)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
                        {feat.detail}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Form to add a new feature */}
                <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '8px' }}>
                    + Tambah Pengalaman / Pilar Baru:
                  </span>
                  <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px', marginBottom: '8px' }}>
                    <select
                      className="unified-input"
                      value={newFeatureIcon}
                      onChange={(e) => setNewFeatureIcon(e.target.value)}
                    >
                      {PROGRAM_ICON_OPTIONS.map((opt, oIdx) => (
                        <option key={oIdx} value={opt.value}>
                          {opt.label.split('(')[0]}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="unified-input"
                      value={newFeatureTitle}
                      onChange={(e) => setNewFeatureTitle(e.target.value)}
                      placeholder="Judul Pengalaman (misal: Peer Connection, Learning Strategies)..."
                    />
                  </div>
                  <textarea
                    className="unified-textarea"
                    rows={2}
                    value={newFeatureDetail}
                    onChange={(e) => setNewFeatureDetail(e.target.value)}
                    placeholder="Deskripsi detail 1-2 kalimat mengenai poin ini..."
                    style={{ marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={handleAddFeature}
                      style={{ fontSize: '12px', padding: '5px 12px' }}
                    >
                      <Plus size={13} /> Tambahkan ke Daftar
                    </button>
                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer Actions */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 20px 20px', position: 'sticky', bottom: 0 }}>
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => setEditorModalOpen(false)}
              >
                Batal
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={handleSaveModalChanges}
                  style={{ background: '#fff' }}
                >
                  Simpan ke Draf
                </button>
                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={handleSaveAndSubmitModal}
                >
                  <Save size={15} /> Simpan & Ajukan Approval
                </button>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
